import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { ConfirmModal } from '../../ui/ConfirmModal';
import { getReleases, createRelease, updateRelease, deleteRelease } from '../../../services/api';
import { useToast } from '../../../hooks/use-toast';
import { Plus } from 'lucide-react';
import axios from 'axios';
import { ReleaseForm } from './ReleaseForm';
import { ReleaseCard } from './ReleaseCard';
import { ExternalReleasesManager } from './ExternalReleasesManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ReleasesManager - Main component for managing music releases
 * Refactored to use sub-components for better maintainability
 */
export const ReleasesManager = () => {
  const { toast } = useToast();
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, release: null });
  const [formData, setFormData] = useState({
    title: '',
    artists: [],
    genre: '',
    artwork: '',
    backCover: '',
    description: '',
    bandcampUrl: '',
    bandcampEmbed: '',
    releaseDate: '',
    soundcloudUrl: '',
    type: 'EP'
  });
  const [uploading, setUploading] = useState(false);
  const [uploadingBack, setUploadingBack] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [backCoverPreview, setBackCoverPreview] = useState('');

  const fetchReleases = useCallback(async () => {
    try {
      const response = await getReleases();
      setReleases(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch releases', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchReleases();
  }, [fetchReleases]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate artists
    if (!formData.artists || formData.artists.length === 0) {
      toast({ title: 'Error', description: 'Please select at least one artist', variant: 'destructive' });
      return;
    }
    
    try {
      if (editingRelease) {
        await updateRelease(editingRelease.id, formData);
        toast({ title: 'Success', description: 'Release updated successfully' });
      } else {
        await createRelease(formData);
        toast({ title: 'Success', description: 'Release created successfully' });
      }
      setIsDialogOpen(false);
      resetForm();
      fetchReleases();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save release', variant: 'destructive' });
    }
  };

  const handleEdit = (release) => {
    setEditingRelease(release);
    setFormData({
      title: release.title,
      artists: release.artists || (release.artist ? [release.artist] : []), // Handle legacy single artist
      genre: release.genre || '',
      artwork: release.artwork,
      backCover: release.backCover || '',
      description: release.description || '',
      bandcampUrl: release.bandcampUrl || '',
      bandcampEmbed: release.bandcampEmbed || '',
      releaseDate: release.releaseDate,
      soundcloudUrl: release.soundcloudUrl,
      type: release.type
    });
    setImagePreview(release.artwork);
    setBackCoverPreview(release.backCover || '');
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deleteRelease(deleteModal.release.id);
      toast({ title: 'Success', description: 'Release deleted successfully' });
      fetchReleases();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete release', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({ 
      title: '', 
      artists: [], 
      genre: '', 
      artwork: '', 
      backCover: '', 
      description: '', 
      bandcampUrl: '', 
      bandcampEmbed: '', 
      releaseDate: '', 
      soundcloudUrl: '', 
      type: 'EP' 
    });
    setEditingRelease(null);
    setImagePreview('');
    setBackCoverPreview('');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'artwork') {
      setImagePreview(e.target.value);
    } else if (e.target.name === 'backCover') {
      setBackCoverPreview(e.target.value);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = response.data.url;
      
      setFormData({ ...formData, artwork: imageUrl });
      setImagePreview(imageUrl);
      toast({ title: 'Success', description: 'Front cover uploaded successfully' });
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: 'Error', description: 'Failed to upload image', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleBackCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingBack(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = response.data.url;
      
      setFormData({ ...formData, backCover: imageUrl });
      setBackCoverPreview(imageUrl);
      toast({ title: 'Success', description: 'Back cover uploaded successfully' });
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: 'Error', description: 'Failed to upload back cover', variant: 'destructive' });
    } finally {
      setUploadingBack(false);
    }
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400">
          Releases Management
        </h1>
        <p className="text-gray-400 mt-2">Manage DharmaR releases and external platform releases</p>
      </div>

      <Tabs defaultValue="dharmar" className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-black/50 border border-green-500/20">
          <TabsTrigger value="dharmar" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
            DharmaR Releases
          </TabsTrigger>
          <TabsTrigger value="external" className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400">
            External Releases
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dharmar" className="space-y-6 mt-6">
          <div className="flex justify-end">
            <Dialog open={isDialogOpen} onOpenChange={(open) => { 
              setIsDialogOpen(open); 
              if (!open) resetForm(); 
            }}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700">
                  <Plus className="w-4 h-4 mr-2" /> Add DharmaR Release
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-black border-orange-500/30 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold sticky top-0 bg-black z-10 py-4">
                    {editingRelease ? 'Edit Release' : 'Add New Release'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                  <ReleaseForm
                    formData={formData}
                    handleChange={handleChange}
                    handleFileUpload={handleFileUpload}
                    handleBackCoverUpload={handleBackCoverUpload}
                    uploading={uploading}
                    uploadingBack={uploadingBack}
                    imagePreview={imagePreview}
                    backCoverPreview={backCoverPreview}
                    setFormData={setFormData}
                  />
                  <Button 
                    type="submit" 
                    disabled={uploading || uploadingBack} 
                    className="w-full bg-gradient-to-r from-orange-500 to-green-500 hover:from-orange-600 hover:to-green-600"
                  >
                    {editingRelease ? 'Update Release' : 'Create Release'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {releases.map((release) => (
              <ReleaseCard
                key={release.id}
                release={release}
                onEdit={handleEdit}
                onDelete={(release) => setDeleteModal({ isOpen: true, release })}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="external" className="mt-6">
          <ExternalReleasesManager />
        </TabsContent>
      </Tabs>

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, release: null })}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete the release "${deleteModal.release?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};
